import { Link } from '@mui/material';
import { t } from 'i18next';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState, PrefilledDataConfig } from '../../../common/controllers/PaginatedTableController';
import { DefaultHeaderConfiguration, FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { PATHS } from '../../../routing/routes';
import { ReportCertificate } from '../models/ReportCertificate';
import { OrganizationCertificatesService } from '../services/OrganizationCertificatesService';

type State = PaginatedTableState & {};

type Props = {
  certificateId: number;
  initialData?: PaginationResult<ReportCertificate> | null;
  parentLoading?: boolean;
  withPrefill?: boolean;
};

export const DEFAULT_MATCHED_CERTIFICATES_PAGE_SIZE = 10;

export class MatchedCertificatesTable extends PaginatedTableController<ReportCertificate, Props, State> {
  private readonly certificatesService: OrganizationCertificatesService;

  static defaultProps = {
    initialData: null,
    parentLoading: false,
    withPrefill: false,
  };

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.certificatesService = new OrganizationCertificatesService();
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.certificatesService.cancelAllRequests();
  }

  protected getPrefilledConfig() {
    if (!this.props.withPrefill) {
      return super.getPrefilledConfig();
    }

    return {
      prefillActive: true,
      initialLoading: this.props.parentLoading,
      initialData: this.props.initialData,
    } as PrefilledDataConfig<ReportCertificate>;
  }

  protected getDefaultPageSize(): number {
    return DEFAULT_MATCHED_CERTIFICATES_PAGE_SIZE;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [];
  }

  protected getDefaultHeaderConfigurations(): DefaultHeaderConfiguration {
    return { title: t('matchedCertificates') };
  }

  protected getFieldConfigurations(): FieldConfiguration<ReportCertificate>[] {
    return [
      {
        fieldKey: 'id',
        label: t('id'),
        description: t('id'),
        columnProps: {
          maxWidth: 100,
        },
        render: (model, { tabIndex }) => (
          <Link
            tabIndex={tabIndex}
            href={UrlHelper.generatePath(PATHS.REPORT_CERTIFICATE, {
              reportId: model.getReport().getId().toString(),
              certificateId: model.getId().toString(),
            })}
            aria-label={
              t('a11y.organization.certificate.matchedCertificates.certificateIdLabel', {
                certId: model.getId(),
                reportId: model.getReport().getId(),
                reportName: model.getReport().getName(),
              }) as string
            }
          >
            {model.getId()}
          </Link>
        ),
      },
      {
        fieldKey: 'project',
        label: t('project'),
        description: t('project'),
        render: (model, { tabIndex, hasFocus }) => {
          const project = model.getProject();
          if (project === null) return <BlankCell blankText={t('noProject')} />;
          return (
            <Link
              tabIndex={tabIndex}
              href={UrlHelper.generatePath(PATHS.PROJECT_DASHBOARD, { projectId: project.getId().toString() })}
              aria-label={
                t('a11y.organization.certificate.matchedCertificates.projectLabel', {
                  projectId: project.getId(),
                  projectName: project.getName(),
                }) as string
              }
            >
              <TypographyOverflow hasFocus={hasFocus}>{project.getName()}</TypographyOverflow>
            </Link>
          );
        },
      },
      {
        fieldKey: 'report',
        label: t('report'),
        description: t('report'),
        render: (model, { tabIndex, hasFocus }) => {
          const report = model.getReport();
          const ariaLabel = t('a11y.organization.certificate.matchedCertificates.reportLabel', { reportId: report.getId(), reportName: report.getName() });
          return (
            <Link tabIndex={tabIndex} href={UrlHelper.generatePath(PATHS.REPORT, { reportId: report.getId().toString() })} aria-label={ariaLabel}>
              <TypographyOverflow hasFocus={hasFocus}>{report.getName()}</TypographyOverflow>
            </Link>
          );
        },
      },
    ];
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<ReportCertificate>> {
    try {
      return await this.certificatesService.getMatchedCertificates(this.props.certificateId, cursorInfo);
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }
}
